import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, ShoppingBag, CheckCircle2, ArrowUpCircle, ExternalLink, Package, Layers } from 'lucide-react';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import { useAddonsWithEntitlements, useOrgEntitlements } from '@/hooks/useLibraryAddons';
import { formatNumber } from '@/utils/format';

const BRAND_LABELS: Record<string, string> = {
  saint_gobain: 'Saint-Gobain',
  knauf: 'Knauf',
  fassa: 'Fassa Bortolo',
};

export default function StoreMyPurchases() {
  const { isLoading, addons } = useAddonsWithEntitlements();
  const { data: entitlements } = useOrgEntitlements();

  if (isLoading) {
    return (
      <SidebarProvider><AppSidebar /><SidebarInset><Header />
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
      </SidebarInset></SidebarProvider>
    );
  }

  // Tutti gli addon che possiedo
  const owned = addons.filter((a) => a.owned);
  // Raggruppo per brand_slug → per ogni brand mostro le versioni che possiedo + se esiste versione più nuova
  const ownedByBrand = owned.reduce<Record<string, typeof owned>>((acc, a) => {
    const k = a.brand_slug ?? 'altro';
    (acc[k] ??= []).push(a);
    return acc;
  }, {});

  // Per ogni brand+category, qual e' la versione corrente disponibile (non-acquistata)?
  // Cosi' possiamo proporre upgrade.
  const upgradeAvailable = (brand_slug: string, category: string, myMaxYear: number) => {
    return addons.find(
      (a) =>
        a.brand_slug === brand_slug &&
        a.category === category &&
        a.is_active &&
        !a.owned &&
        (a.version_year ?? 0) > myMaxYear,
    );
  };

  const totalSpent = (entitlements ?? [])
    .filter((e) => !!e.lemonsqueezy_order_id)
    .reduce((sum, e) => {
      const a = addons.find((x) => x.id === e.addon_id);
      return sum + (a ? Number(a.price_eur) : 0);
    }, 0);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <Header />
        <div className="p-6 max-w-5xl mx-auto w-full">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <ShoppingBag className="h-7 w-7 text-primary" />
                <h1 className="text-2xl font-bold text-zinc-900">I miei acquisti</h1>
              </div>
              <p className="text-zinc-600">Storico delle librerie acquistate per la tua organizzazione, con versioni e disponibilità di aggiornamenti.</p>
            </div>
            <Card className="shrink-0">
              <CardContent className="p-4 text-center">
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Totale speso</div>
                <div className="text-2xl font-bold text-zinc-900">€{totalSpent.toLocaleString('it-IT')}</div>
              </CardContent>
            </Card>
          </div>

          {Object.keys(ownedByBrand).length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <ShoppingBag className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
                <h3 className="font-semibold text-zinc-900 mb-1">Nessun acquisto ancora</h3>
                <p className="text-sm text-zinc-500 mb-4">Vai sullo Store per esplorare i listini brand e le stratigrafie certificate.</p>
                <Button asChild>
                  <Link to="/store">Esplora lo Store →</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {Object.entries(ownedByBrand).map(([brand, items]) => (
                <Card key={brand}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{BRAND_LABELS[brand] ?? brand}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {items.map((item) => {
                      const myYear = item.version_year ?? 0;
                      const upgrade = item.brand_slug
                        ? upgradeAvailable(item.brand_slug, item.category, myYear)
                        : null;
                      return (
                        <div key={item.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded border border-zinc-200">
                          <div className="flex items-center gap-2 min-w-0">
                            {item.category === 'brand' && <Package className="h-4 w-4 text-blue-600 shrink-0" />}
                            {item.category === 'stratigraphies' && <Layers className="h-4 w-4 text-purple-600 shrink-0" />}
                            {item.category === 'bundle' && <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />}
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-zinc-900 truncate">{item.name}</div>
                              <div className="text-[11px] text-zinc-500">
                                v{item.version_year ?? '?'}
                                {item.is_current_year ? ' · versione corrente' : ' · versione archivio (storico)'}
                              </div>
                            </div>
                          </div>
                          {upgrade && upgrade.upgrade_price_eur != null && (
                            <Button asChild size="sm" variant="outline" className="border-amber-200 text-amber-700 hover:bg-amber-50">
                              <Link to="/store">
                                <ArrowUpCircle className="h-3.5 w-3.5 mr-1" />
                                Aggiorna a v{upgrade.version_year} (€{formatNumber(upgrade.upgrade_price_eur, 0)})
                              </Link>
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              ))}

              <Card className="bg-zinc-50 border-zinc-200">
                <CardContent className="p-4 text-sm text-zinc-600 flex items-start gap-3">
                  <ExternalLink className="h-4 w-4 mt-0.5 text-zinc-400 shrink-0" />
                  <div>
                    <strong className="text-zinc-900">Ogni libreria che possiedi resta tua per sempre.</strong>
                    <br />
                    Non c'è una scadenza. Quando esce una nuova versione annuale ti proponiamo l'upgrade scontato (40% del prezzo pieno) ma puoi continuare a usare la tua versione attuale indefinitamente. I preventivi storici mantengono sempre i prezzi del listino al momento della creazione.
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
